'use client';

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  InquiryFlow,
  InquiryFlowSkeleton,
  type FlowDocument,
  type ItemPickerInstance,
  type ItemPickerItem,
  type RecordedVideoValue,
} from '@miso/design-system';
import { Box, CircularProgress } from '@mui/material';
import { movingItemApi } from '@/entities/moving-item';
import type { Spec } from '@/entities/moving-item/model/moving-item.types';
import {
  specToFlowDocument,
  instanceToMovingItemRequest,
  maxServiceSetLabel,
} from '@/features/moving-item/spec-to-flow/specToFlowDocument';
import { useMovingItemSpecs } from '@/features/moving-item/fetch-specs';
import {
  SERVICE_SET_VALUES,
  SERVICE_SET_LABELS,
  type MovingItemRequest,
  type ServiceSet,
} from '@/entities/visit-counsel/model/room-observing.types';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { RoomTypeValue } from '@/entities/visit-counsel/model/house-sketch.types';
import { movingItemsToInstances } from '../model/instancesPrefill';
import { runObservingVideoUploadWithPersistence } from '../model/observingVideoUploadPipeline';
import { dedupeOrdinalPrefix } from '../model/dedupeOrdinalPrefix';
import { buildMovingItemPickerSections } from '@miso/counsel-schema';
import { useObservingSessionPendingActions } from '@/features/visit-counsel/room-observing-flow/model/pendingObservingSessions';
import type { EditTarget } from '@/features/visit-counsel/room-observing-flow/ui/EditTargetSelector';

// ── Constants ────────────────────────────────────────────────────────────────

const ITEM_PICKER_QUESTION_ID = 'movingItems';
const VIDEO_QUESTION_ID = 'roomVideo';
const TERMINAL_ID = 'moving-items-saved';
const PICK_STEP_ID = 'pick';
const VIDEO_STEP_ID = 'record-video';

// ── 단일 섹션 FlowDocument 빌더 (root flow) ─────────────────────────────────

interface BuildOptions {
  specs: Spec[];
  roomLabel: string;
  /** 기록 중인 방 타입 — roomTypes 기준 ItemPicker 섹션 분류 */
  roomType?: RoomTypeValue;
  /** REQUESTED 세션(첫 시작) — 영상 촬영을 강제. 편집 모드(false)에서는 선택. */
  isFirstStart: boolean;
  /** 편집 대상. undefined 이면 기존 full 플로우(items → video → terminal) */
  editTarget?: EditTarget;
  /**
   * 추가공간(`isAdditionalSpaceMoving=true`) 전용. 짐 기록을 받지 않는 추가공간
   * (사다리차/계단/엘리베이터 등) 일 때 false 로 전달한다. items step 을 강제로
   * 건너뛰고 영상 step 부터 시작하며, `handleComplete` 도 짐 저장을 생략한다.
   *
   * undefined 또는 true 면 기존 동작.
   */
  canRecordMovingItems?: boolean;
}

function buildMovingItemsFlow({
  specs,
  roomLabel,
  roomType,
  isFirstStart,
  editTarget,
  canRecordMovingItems,
}: BuildOptions): FlowDocument {
  // 호출 측(RoomObservingFlowAdapter 등)이 만든 라벨에 ordinal prefix 가 두 번 누출된
  // 케이스("1번1번 방")가 있어 표시 직전에 한 번 더 정제한다. 정상 라벨은 그대로 통과.
  const safeRoomLabel = dedupeOrdinalPrefix(roomLabel);
  const sections = buildMovingItemPickerSections({
    specs,
    currentRoomType: roomType,
    toItem: (spec) => {
      // description 은 spec 자체의 설명 대신, 해당 짐이 제공할 수 있는 가장 높은
      // 서비스 범위(예: "분해/운반/설치 가능")로 채운다 — 사용자가 카드만 보고
      // 짐별 서비스 한도를 인지할 수 있도록 한다.
      const description = maxServiceSetLabel(spec);
      return {
        id: spec.id,
        title: spec.name,
        ...(description ? { description } : {}),
        ...(spec.iconDownloadUrl ? { icon: spec.iconDownloadUrl } : {}),
        flow: spec.id, // sub-flow id → flowFetcher 가 server에서 spec 단건 fetch
      };
    },
  });

  // editTarget 에 따라 포함할 step 결정.
  //   - undefined / 'both': items + video (full 플로우)
  //   - 'items'           : items step 만 (video 스킵)
  //   - 'video'           : video step 만 (items 스킵)
  // canRecordMovingItems === false (추가공간 전용) 이면 editTarget 과 무관하게
  // items step 을 강제 제외하고 video step 만 남긴다.
  const isVideoOnlyForAdditionalSpace = canRecordMovingItems === false;
  const includeItems =
    !isVideoOnlyForAdditionalSpace &&
    (!editTarget || editTarget === 'items' || editTarget === 'both');
  const includeVideo =
    isVideoOnlyForAdditionalSpace || !editTarget || editTarget === 'video' || editTarget === 'both';

  const pickStep: FlowDocument['steps'][string] = {
    kind: 'questions',
    questions: [
      {
        id: ITEM_PICKER_QUESTION_ID,
        // 짐 기록 root 흐름은 L2 layout — sub-flow(짐 spec) 의 attribute/serviceSet
        // 질문도 동일하게 L2 로 통일되어 cascade 경험을 제공한다.
        // (record-video step만 video-full 로 별도 지정.)
        layout: 'L2',
        // 줄바꿈(`\n`)은 L2 의 `whitespace-pre-line` 으로 두 줄 렌더된다.
        title: `${safeRoomLabel}에서 가져가실 짐을\n선택해주세요`,
        // 사용자가 카드를 탭해 instances 가 늘어나면 description 이
        // "쇼파, 침대" → "쇼파, 침대, 책상 외 N개" 형태로 동적 갱신된다.
        description: {
          summary: {
            sourceQuestionId: ITEM_PICKER_QUESTION_ID,
            headCount: 3,
          },
        },
        input: {
          kind: 'item-picker',
          required: false,
          multiple: true,
          sections,
          // Figma 205-9091: "선택한 짐 목록" 시트 헤더.
          reviewTitle: '선택한 짐 목록',
          // Figma 205-9090: 메인 picker footer 의 세컨더리 텍스트 — 선택된 짐
          // 인스턴스가 1개 이상일 때만 노출되며 클릭 시 위 reviewTitle 시트가 열린다.
          reviewButtonLabel: '짐 확인하기',
          roomLabel: safeRoomLabel,
          allowEmptyProceed: true,
          // 빈 상태 다이얼로그 워딩 — 방 라벨이 보간된 최종 문자열을 직접 전달한다.
          // (Renderer 의 fallback 도 roomLabel 을 description 에만 보간하지만, title 은
          // hardcoded "선택한 짐이 없어요" 로 떨어지므로 도메인 일관성을 위해 명시.)
          emptyTitle: `${safeRoomLabel}에서 가져가실 짐이 없어요`,
          emptyDescription: `${safeRoomLabel}에서 가져가실 짐이 없어요.\n짐 없이 진행하시겠어요?`,
          // 서비스 범위 모드 탭(modes/modeAnswerKey)은 운영 반영 보류 — 플래너 교육
          // 전이라 주입하지 않는다. 스키마·컴포넌트는 modes 없이 기존 동작 그대로다.
        },
      },
    ],
    // items 만 편집일 때 video 스텝을 건너뛰고 바로 terminal 로.
    next: includeVideo
      ? { kind: 'goto', target: VIDEO_STEP_ID }
      : { kind: 'terminal', target: TERMINAL_ID },
  };

  const videoStep: FlowDocument['steps'][string] = {
    kind: 'questions',
    questions: [
      {
        id: VIDEO_QUESTION_ID,
        // VideoFullLayout 은 input 이 전체 화면을 채우고 AppBar overlay만 노출.
        // 제목/설명은 무시된다 — appBarTitle 로 방 라벨만 노출.
        layout: 'video-full',
        appBarTitle: safeRoomLabel,
        title: `${safeRoomLabel} 영상`,
        input: {
          kind: 'video-recorder',
          // 첫 시작은 영상 필수, 편집 모드는 선택(미녹화 시 그대로 다음 진행).
          // 편집 모드에서 사용자가 다시 찍으면 새 영상이 업로드된다.
          required: isFirstStart,
          preferUltraWide: true,
          defaultFacingMode: 'environment',
          autoRequestPermission: true,
        },
      },
    ],
    next: { kind: 'terminal', target: TERMINAL_ID },
  };

  const steps: FlowDocument['steps'] = {};
  if (includeItems) steps[PICK_STEP_ID] = pickStep;
  if (includeVideo) steps[VIDEO_STEP_ID] = videoStep;

  return {
    schemaVersion: 1,
    id: 'moving-items-record-flow',
    initial: includeItems ? PICK_STEP_ID : VIDEO_STEP_ID,
    steps,
    terminals: {
      [TERMINAL_ID]: { kind: 'placeholder-auto' },
    },
  };
}

// ── Adapter ─────────────────────────────────────────────────────────────────

export interface MovingItemsFlowAdapterProps {
  /** 상담 ID */
  visitCounselId: string;
  /** room 또는 additional space 세션 ID */
  sessionId: string;
  /** additional space (현관/추가공간 등) 모드 여부 */
  isAdditionalSpaceMoving: boolean;
  /** 세션이 REQUESTED 상태(첫 시작) → start API, 아니면 record API */
  isFirstStart: boolean;
  /** UI 라벨 ("거실", "1번 방" 등) */
  roomLabel: string;
  /** 기록 중인 방 타입 — spec.roomTypes 기준 ItemPicker 섹션 분류 */
  roomType?: RoomTypeValue;
  /** 기존 저장된 짐 (편집 모드 prefill) */
  initialMovingItems?: MovingItemRequest[];
  /**
   * 완료된 방 편집 시 편집 대상.
   *
   * - 'items': 짐 기록만 편집 — video step 스킵, 짐 저장 API 만 호출
   * - 'video': 영상만 편집  — items pick step 스킵, video 업로드 API 만 호출
   * - 'both' / undefined  : 기존 full 플로우 (items → video)
   */
  editTarget?: EditTarget;
  /**
   * 추가공간(`isAdditionalSpaceMoving=true`) 전용. 짐 기록을 받지 않는 추가공간
   * (사다리차 진입로/계단/엘리베이터/1층 트럭 진입로 등) 에서 false 로 전달한다.
   *
   * - false 이면 editTarget 과 무관하게 items step 을 강제로 건너뛰고 영상만 진행한다.
   * - 첫 시작(`isFirstStart=true`)이면 영상 업로드 직전에 빈 movingItems 로
   *   `startAdditionalSpaceObservingSession` 을 호출해 세션을 STARTED 상태로 전환한다
   *   (옛 `CounselRoomRecordPage` 의 `canRecordMovingItems !== true` 분기와 동일 시퀀스).
   *
   * undefined 또는 true 면 기존 동작 (메인 방 + 짐 기록 가능 추가공간).
   */
  canRecordMovingItems?: boolean;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  /** 저장 후 최신 counsel 을 가져오는 refetch (SWR mutate wrapper) */
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  /**
   * 짐/영상 저장 직후 picker 로 복귀시키는 콜백.
   *
   * 본 어댑터는 저장 API 가 반환한 직후 한 번만 refetch 한 뒤 그 counsel 로
   * 즉시 호출한다 (status='STARTED' 인 응답이라도 일단 picker 표시).
   * 영상 반영 지연 등으로 done 배지가 부족한 경우는 백그라운드 폴링이 SWR
   * 캐시를 추가로 mutate 하므로 picker 가 자동 리렌더된다.
   */
  onAfterDone: (counsel: VisitCounselView) => void;
  /** 사용자 취소(뒤로가기) */
  onCancel: () => void;
  /** Inquiry 내부 BottomSheet portal target (없으면 body) */
  container?: HTMLElement | null;
}

/**
 * 이사 짐 기록 페이지의 InquiryFlow 어댑터.
 *
 * 흐름:
 *   1. useMovingItemSpecs 로 spec list 를 가져와 root item-picker step 구성.
 *   2. 짐 클릭 시 flowFetcher 가 server 에서 spec 단건 + compile() 결과를 받아
 *      specToFlowDocument 로 sub-flow 변환 → InquiryFlow 가 sub-flow 실행.
 *      - GUIDE_MESSAGE → conditional description (자동)
 *      - allowedServiceSets → service-set 옵션 좁힘
 *   3. 모든 짐 picking 완료 후 review → terminal "moving-items-saved" 도달 즉시
 *      onComplete 트리거 → 저장 API → (영상 저장) → 즉시 refetch + onAfterDone
 *      → 영상 케이스는 백그라운드 폴링으로 done 배지 추후 반영.
 *
 * 빈 짐 저장은 BottomSheet 의 emptyTitle/emptyDescription 을 통해 안내한 뒤
 * 그대로 저장 진행 (확인 dialog 없이) — 기존 페이지의 추가 confirm 단계는 생략.
 */
export function MovingItemsFlowAdapter({
  visitCounselId,
  sessionId,
  isAdditionalSpaceMoving,
  isFirstStart,
  roomLabel,
  roomType,
  initialMovingItems,
  editTarget,
  canRecordMovingItems,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
  container,
}: MovingItemsFlowAdapterProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLElement | null>(container ?? null);
  containerRef.current = container ?? wrapperRef.current;

  const { specs, isLoading: specsLoading, error: specsError } = useMovingItemSpecs();

  // 백그라운드 폴링 중에는 picker 의 해당 방 카드를 dimmed + spinner 처리한다.
  // 폴링 시작 직전 markPending, then/catch/finally 에서 clearPending 호출.
  const { markPending, clearPending } = useObservingSessionPendingActions();

  const flow = useMemo(
    () =>
      specs.length > 0
        ? buildMovingItemsFlow({
            specs,
            roomLabel,
            roomType,
            isFirstStart,
            editTarget,
            canRecordMovingItems,
          })
        : null,
    [specs, roomLabel, roomType, isFirstStart, editTarget, canRecordMovingItems]
  );

  const initialValues = useMemo(() => {
    if (!initialMovingItems || initialMovingItems.length === 0) return undefined;
    return { [ITEM_PICKER_QUESTION_ID]: movingItemsToInstances(initialMovingItems) };
  }, [initialMovingItems]);

  const specById = useMemo(() => new Map(specs.map((s) => [s.id, s])), [specs]);

  // prefill된 인스턴스는 sub-flow doc이 로드되지 않아 review sheet에서 key 값이 노출된다.
  // resolveInstanceSummary prop으로 도메인 지식(SERVICE_SET_LABELS, enumDisplayNames)을
  // 이용해 한국어 요약 문자열을 직접 생성한다.
  const instanceSummary = useCallback(
    (instance: ItemPickerInstance, _item: ItemPickerItem): string | null => {
      const spec = specById.get(instance.itemId);
      if (!spec) return null;
      const answers = instance.answers as Record<string, unknown>;
      const serviceSet = answers['serviceSet'] as ServiceSet | undefined;
      const serviceLabel = serviceSet ? (SERVICE_SET_LABELS[serviceSet] ?? serviceSet) : null;
      const attrParts: string[] = [];
      for (const attr of spec.attributes) {
        const raw = answers[`attr__${attr.key}`];
        if (raw == null || raw === '') continue;
        if (attr.type === 'ENUM') {
          const label = attr.enumDisplayNames?.[String(raw)] ?? String(raw);
          attrParts.push(label);
        } else {
          attrParts.push(String(raw));
        }
      }
      const parts = [...attrParts, ...(serviceLabel ? [serviceLabel] : [])];
      return parts.length > 0 ? parts.join(' · ') : null;
    },
    [specById]
  );

  /**
   * sub-flow 동적 fetch.
   * - getSpec(specId): 최신 spec 정의 (name/attributes/conditions/displayTemplateRules 등)
   * - compile(specId, {}): 빈 attribute 기준 초기 conditionEffects.allowedServiceSets
   *   (compile 실패 시 모든 ServiceSet 허용으로 fallback)
   * 두 결과를 specToFlowDocument(spec, allowedServiceSets) 으로 변환.
   */
  const handleFlowFetch = useCallback(
    async (flowId: string, context?: Record<string, unknown>): Promise<FlowDocument> => {
      const spec = await movingItemApi.getSpec(flowId);
      // 활성 모드가 있으면 그 서비스로 프리셋 → 무조건 허용 시 서비스 질문 생략(자동 적용),
      // 조건부면 유효 옵션만 활성(나머지 dim)한 단일 서비스 질문을 생성한다.
      const presetServiceSet = context?.activeModeId as ServiceSet | undefined;
      return specToFlowDocument(spec, { presetServiceSet });
    },
    []
  );

  /**
   * terminal "moving-items-saved" 도달 즉시 호출됨 (placeholder-auto terminal).
   *
   * editTarget 에 따라 저장 대상을 구분한다:
   *   - 'items' : 짐 저장 API 만 호출 (video 없음, completeSession 없음)
   *   - 'video' : video 업로드 + recordVideo API 만 호출 (짐 저장 없음)
   *   - 'both' / undefined : 기존 full 플로우 (짐 저장 → video 업로드 → complete)
   *
   * 두 단계의 결과를 함께 처리한다:
   *   1. movingItems  — accumulated[ITEM_PICKER_QUESTION_ID] 의 instances → MovingItemRequest[]
   *   2. roomVideo    — accumulated[VIDEO_QUESTION_ID] 의 RecordedVideoValue (선택, 편집 모드는 nullable)
   *
   * 흐름:
   *   - 짐 저장 (start 또는 record API)
   *   - 영상이 있으면: presigned upload → recordVideo API → completeSession API
   *   - 즉시 refetch 한 번 → onAfterDone(counsel) 으로 picker 즉시 복귀
   *   - 영상 저장 케이스는 백그라운드에서 추가 폴링 → SWR mutate 로 done 배지 자동 반영
   */
  const handleComplete = useCallback(
    async (accumulated: Record<string, unknown>) => {
      // canRecordMovingItems === false 인 추가공간(사다리차/계단 등) 은 짐 단계 자체가 없다.
      // editTarget 의 'items' / 'both' 도 무시하고 항상 video 만 저장한다.
      const isVideoOnlyForAdditionalSpace =
        isAdditionalSpaceMoving && canRecordMovingItems === false;
      const saveItems =
        !isVideoOnlyForAdditionalSpace &&
        (!editTarget || editTarget === 'items' || editTarget === 'both');
      const saveVideo =
        isVideoOnlyForAdditionalSpace ||
        !editTarget ||
        editTarget === 'video' ||
        editTarget === 'both';

      const instances =
        (accumulated[ITEM_PICKER_QUESTION_ID] as ItemPickerInstance[] | undefined) ?? [];
      // quantity > 1인 인스턴스는 같은 설정을 quantity 개만큼 복제해 API에 전달한다.
      const expandedInstances = instances.flatMap((inst) => {
        const qty = inst.quantity ?? 1;
        if (qty <= 1) return [inst];
        return Array.from({ length: qty }, (_, i) =>
          i === 0 ? inst : { ...inst, instanceId: crypto.randomUUID() }
        );
      });
      const items: MovingItemRequest[] = expandedInstances
        .map(instanceToMovingItemRequest)
        // serviceSet 누락 (sub-flow를 끝까지 안 돌린 경우)은 안전하게 필터.
        .filter((it) => SERVICE_SET_VALUES.includes(it.serviceSet as ServiceSet));

      const recordedVideo =
        (accumulated[VIDEO_QUESTION_ID] as RecordedVideoValue | null | undefined) ?? null;

      try {
        let itemsPersistedBeforeUpload = false;

        // ── (1) 짐 기록 저장 ─────────────────────────────────────────────────
        if (saveItems) {
          if (isAdditionalSpaceMoving) {
            if (isFirstStart) {
              await myVisitCounselApi.startAdditionalSpaceObservingSession(
                visitCounselId,
                sessionId,
                { movingItems: items }
              );
            } else {
              await myVisitCounselApi.recordAdditionalSpaceObservingMovingItems(
                visitCounselId,
                sessionId,
                { movingItems: items }
              );
            }
          } else {
            if (isFirstStart) {
              await myVisitCounselApi.startRoomObservingSession(visitCounselId, sessionId, {
                movingItems: items,
              });
            } else {
              await myVisitCounselApi.recordRoomObservingMovingItems(visitCounselId, sessionId, {
                movingItems: items,
              });
            }
          }
          itemsPersistedBeforeUpload = true;
        } else if (
          isVideoOnlyForAdditionalSpace &&
          isFirstStart &&
          saveVideo &&
          recordedVideo?.blob
        ) {
          // 추가공간(canRecordMovingItems=false) 첫 시작: 영상 업로드 직전에 빈 짐으로 세션을 STARTED 로 전환.
          await myVisitCounselApi.startAdditionalSpaceObservingSession(visitCounselId, sessionId, {
            movingItems: [],
          });
          itemsPersistedBeforeUpload = true;
        }

        // ── (2) 영상: presigned 업로드는 IndexedDB 에 남겨 새로고침 후 재개 가능 ─
        if (saveVideo && recordedVideo?.blob) {
          await runObservingVideoUploadWithPersistence(
            {
              visitCounselId,
              sessionId,
              blob: recordedVideo.blob,
              mimeType: recordedVideo.mimeType || 'video/webm',
              isAdditionalSpaceMoving,
              isFirstStart,
              saveItems,
              editTarget,
              isVideoOnlyForAdditionalSpace,
              items,
              itemsPersistedBeforeUpload,
            },
            {
              showToast,
              refetch,
              markPending,
              clearPending,
              onAfterDone,
              onCancel,
            }
          );
          return;
        }

        // ── 짐만 저장 (영상 없음) ───────────────────────────────────────────
        const hasVideo = false;
        if (editTarget === 'items') {
          showToast('이사 짐 기록이 수정되었습니다.', 'success');
        } else if (editTarget === 'video' || isVideoOnlyForAdditionalSpace) {
          showToast(
            hasVideo
              ? isFirstStart
                ? '영상이 저장되었습니다.'
                : '영상이 수정되었습니다.'
              : '영상 수정이 건너뛰어졌습니다.',
            'success'
          );
        } else if (isFirstStart) {
          showToast('이사 짐 기록이 저장되었습니다.', 'success');
        } else {
          showToast('이사 짐 기록이 수정되었습니다.', 'success');
        }

        const initial = await refetch();
        const initialCounsel = initial?.counsel ?? null;
        if (initialCounsel) onAfterDone(initialCounsel);
        else onCancel();
      } catch (err) {
        showToast(err instanceof Error ? err.message : '저장에 실패했습니다', 'error');
        onCancel();
      }
    },
    [
      visitCounselId,
      sessionId,
      isAdditionalSpaceMoving,
      isFirstStart,
      editTarget,
      canRecordMovingItems,
      showToast,
      refetch,
      onAfterDone,
      onCancel,
      markPending,
      clearPending,
    ]
  );

  const handleError = useCallback(
    (err: Error) => {
      showToast(err.message ?? '플로우 오류가 발생했습니다', 'error');
      onCancel();
    },
    [showToast, onCancel]
  );

  // 렌더 중 showToast를 직접 호출하면 "setState during render" 에러 발생
  // useEffect에서 호출해야 한다
  useEffect(() => {
    if (specsError) {
      showToast(specsError.message, 'error');
    }
  }, [specsError, showToast]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      {specsLoading || !flow ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
        >
          {specsLoading ? <CircularProgress /> : <InquiryFlowSkeleton />}
        </Box>
      ) : (
        <InquiryFlow
          flow={flow}
          initialValues={initialValues}
          registries={{ terminals: {} }}
          flowFetcher={handleFlowFetch}
          onComplete={handleComplete}
          onCancel={onCancel}
          onError={handleError}
          container={containerRef.current}
          resolveInstanceSummary={instanceSummary}
        />
      )}
    </div>
  );
}
